import React, { useContext, useState, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import { ClientMarket } from '@prisjakt/web-app-config/types/client-market';
import { useEffectOnMount } from '../../../../hooks/useEffectOnMount';
import RouteContext from '../../../../routes/context';
import VariantGroups from '../variant-groups';
import {
  productPriceClick,
  productClickOutBounce,
  setSelectedVariant,
} from '../../actions/product';
import { AppState } from '../../../../reducers';
import { initialFilterState } from '../../constants';
import { getPriceFilters } from './utils';
import {
  trackPriceListFilterUpdate,
  trackProductVariantClick,
} from '../tracking';
import { Variant as VariantType, VariantGroup } from '../../../../types';
import PriceListPanel from './price-list-panel';
import { PriceListProps, PriceListStateProps } from './types';

const PriceList: React.FC<PriceListProps> = props => {
  const {
    id,
    category,
    me,
    prices,
    dispatchProductPriceClick,
    selectedVariant,
    variantGroups,
    ...priceListPanelProps
  } = props;

  const { t, market } = useContext(AppContext);
  const { dispatch: routeDispatch } = useContext(RouteContext);
  const dispatch = useDispatch();
  const [junkFilter, setJunkFilters] = useState(initialFilterState);
  const [featuredAlternativePriceType] = useState<string>(
    defaultFeaturedAlternativePriceType(market)
  );

  const [hiddenListOptions] = useState<string[]>(
    defaultHiddenListOptions(market)
  );

  const priceFilters = getPriceFilters(
    prices,
    t,
    junkFilter,
    featuredAlternativePriceType,
    hiddenListOptions
  );
  const hasVariantGroups =
    variantGroups &&
    variantGroups.length > 0 &&
    market.productVariantCategories?.find(c => c === category?.id);

  const quickFilters = priceFilters.filter(elem => elem.showAsQuickFilter);

  const handlePriceFilterCheckboxChange = useCallback(
    e => {
      const isQuickFilter = Boolean(
        quickFilters.filter(quickFilter => quickFilter.name === e.target.name)
          .length
      );

      const selectedFilter = priceFilters.find(
        priceFilter => priceFilter.name === e.target.name
      );

      if (selectedFilter && selectedFilter.type)
        trackPriceListFilterUpdate(selectedFilter.type, isQuickFilter);

      routeDispatch({
        type: 'PRICE_FILTER',
        options: {
          ...junkFilter,
          [e.target.name]: e.target.checked,
        },
        // selectedVariant.id === 0 corresponds to "all", which should not be fetched by id
        variants:
          selectedVariant && selectedVariant.id !== 0
            ? [selectedVariant.id]
            : null,
      });

      setJunkFilters({
        ...junkFilter,
        [e.target.name]: e.target.checked,
      });
    },
    [junkFilter, priceFilters, routeDispatch, quickFilters, selectedVariant]
  );

  const handleSelectedVariant = (
    group: VariantGroup,
    variant: VariantType | null
  ) => {
    dispatch(setSelectedVariant(variant));
    trackProductVariantClick(group, variant);
    routeDispatch({
      type: 'PRODUCT_VARIANTS',
      variants: variant && variant.id !== 0 ? [variant.id] : null,
      options: { ...junkFilter },
    });
  };

  useEffectOnMount(() => {
    if (!prices?.nodes?.length) {
      // If initial fetch returns no prices, fetch prices again with showUsedProducts: true
      routeDispatch({
        type: 'PRICE_FILTER',
        options: {
          ...junkFilter,
          showUsedProducts: true,
        },
        variants: null,
      });

      setJunkFilters({
        ...junkFilter,
        showUsedProducts: true,
      });
    }
  });

  return (
    <>
      {hasVariantGroups && (
        <VariantGroups
          handleSelectedVariant={handleSelectedVariant}
          variantGroups={variantGroups!}
        />
      )}
      <PriceListPanel
        me={me}
        productId={id}
        handlePriceFilterCheckboxChange={handlePriceFilterCheckboxChange}
        priceFilters={priceFilters}
        quickFilters={quickFilters}
        showProductInformationTexts={market.showProductInformationTexts}
        prices={prices}
        storeStatistics={prices?.meta?.storeStatistics}
        junkFilter={junkFilter}
        dispatchProductPriceClick={dispatchProductPriceClick}
        category={category}
        {...priceListPanelProps}
      />
    </>
  );
};

const defaultHiddenListOptions = (market: ClientMarket) => {
  return market.priceList && market.priceList.hiddenListOptions
    ? [...market.priceList.hiddenListOptions]
    : [];
};

const defaultFeaturedAlternativePriceType = (market: ClientMarket) => {
  return market.priceList && market.priceList.featuredAlternativePriceType
    ? market.priceList.featuredAlternativePriceType
    : 'international';
};

const mapStateToProps = (state: AppState): PriceListStateProps => ({
  me: state.areas.account.me,
  selectedVariant: state.areas.product.variant,
  currentSort: state.areas.product.sort,
  currentGrouping: state.areas.product.grouping,
});

const dispatchProps = {
  dispatchProductPriceClick: productPriceClick,
  dispatchProductClickOutBounce: productClickOutBounce,
};

export default connect(mapStateToProps, dispatchProps)(PriceList);
